import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  HStack,
  Image,
  Stack,
  Text,
  useBoolean,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import allfredLogo from "@src/assets/img/full-logo.svg";
import { LoadingOverlay } from "@src/components/ui-kit";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

const QUOTE_APPROVAL_URL = "/quote-approval";

type QuoteApprovalScreenProps = {
  id: string;
};

type PublicQuoteResponse = {
  logo: string;
  quote_url: string;
  status: 1 | 0 | null;
};

export const QuoteApprovalScreen = observer(function QuoteApprovalScreen({
  id,
}: QuoteApprovalScreenProps) {
  const { authStore } = useStore();
  const toast = useToast();
  const [quote, setQuote] = useState<PublicQuoteResponse | null>(null);
  const [isLoading, setIsLoading] = useBoolean(false);
  const [isApproving, setIsApproving] = useBoolean(false);
  const [isRejecting, setIsRejecting] = useBoolean(false);
  const status = quote?.status ?? null;

  const fetchQuote = async () => {
    setIsLoading.on();
    try {
      const res = await authStore.apiRequest(
        `${QUOTE_APPROVAL_URL}?public_id=${id}`,
        undefined,
        "GET",
      );
      if (res.status !== 200) {
        toast({
          title: t`Failed to fetch quote`,
          status: "error",
        });
        setIsLoading.off();
        return;
      }
      setQuote((await res.json()) as PublicQuoteResponse);
    } catch (_e) {
      toast({
        title: t`Failed to fetch quote`,
        status: "error",
      });
    }
    setIsLoading.off();
  };

  const changeApprovalStatus = async (status: 1 | 0) => {
    const res = await authStore.apiRequest(
      `${QUOTE_APPROVAL_URL}?public_id=${id}&status=${status}`,
      undefined,
      "GET",
    );

    if (res.status !== 200) {
      return toast({
        title: t`Failed to change quote approval status`,
        status: "error",
      });
    }

    setQuote((prev) =>
      prev
        ? {
            status,
            logo: prev.logo,
            quote_url: prev.quote_url,
          }
        : null,
    );
    return toast({
      title: t`Quote approval status changed`,
      status: "success",
    });
  };

  const acceptQuote = async () => {
    setIsApproving.on();
    await changeApprovalStatus(1);
    setIsApproving.off();
  };
  const rejectQuote = async () => {
    setIsRejecting.on();
    await changeApprovalStatus(0);
    setIsRejecting.off();
  };

  useEffect(() => {
    if (!id) return;
    fetchQuote();
  }, [id]);

  return (
    <VStack w="100vw" minH="100vh" px="4" py="12">
      <LoadingOverlay isLoading={isLoading} w="full">
        <Stack spacing="6">
          <HStack justify="space-between" wrap="wrap">
            {quote?.logo && (
              <Image w="90px" alt={t`agency logo`} src={quote.logo} />
            )}
            <VStack align="center" w="full">
              <Heading>
                <Trans>Quote Approval</Trans>
              </Heading>
              <Text>
                <Trans>
                  Review the details of the proposed quote below. Please select
                  'Accept' or 'Reject' to proceed.
                </Trans>
              </Text>
            </VStack>
            <Box />
          </HStack>
          {!isLoading && (
            <VStack spacing="6">
              {status === null ? (
                <HStack spacing="3">
                  <Button
                    colorScheme="green"
                    isLoading={isApproving}
                    onClick={acceptQuote}
                    size="lg"
                  >
                    <Trans>Accept Quote</Trans>
                  </Button>
                  <Button
                    colorScheme="red"
                    isLoading={isRejecting}
                    onClick={rejectQuote}
                    size="lg"
                  >
                    <Trans>Reject Quote</Trans>
                  </Button>
                </HStack>
              ) : (
                <Alert
                  maxW="300px"
                  rounded="lg"
                  status={status === 1 ? "success" : "error"}
                >
                  <AlertIcon />
                  <Text>
                    <Trans>
                      Quote was {status === 1 ? "accepted" : "rejected"}
                    </Trans>
                  </Text>
                </Alert>
              )}
              <Box
                as="object"
                w="full"
                maxW="1100px"
                h="850"
                data={quote?.quote_url}
              />
            </VStack>
          )}
        </Stack>
      </LoadingOverlay>
      <HStack
        pos="fixed"
        right="0"
        bottom="0"
        left="0"
        align="baseline"
        w="full"
        p="6"
        bg="white"
        borderColor="gray.100"
        borderTopWidth="2px"
        spacing="4"
      >
        <Text>
          <Trans>Powered by</Trans>
        </Text>
        <Image pos="relative" w="150" alt={t`Allfred Logo`} src={allfredLogo} />
        <Text>
          <Trans>Empowering Agencies to Work Smarter, Not Harder.</Trans>
        </Text>
      </HStack>
    </VStack>
  );
});
